'use client';

import { PreviewCard } from '@base-ui/react/preview-card';
import { BookOpen, Calendar, ExternalLink, Star } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useState, type ChangeEvent, type MouseEvent } from 'react';

import { cn } from '@/shared/utils/cn';

import { CourseCardCartIconButton } from './CourseCardCartIconButton';
import { CourseCardDealIcon } from './CourseCardDealIcon';
import { CourseCardDiscountBadge } from './CourseCardDiscountBadge';
import { CourseCardFavoriteButton } from './CourseCardFavoriteButton';
import { CourseCardPopover } from './CourseCardPopover';
import { CourseCardAllianceNote } from './CourseCardAllianceNote';
import { CourseCardAllianceTag } from './CourseCardAllianceTag';
import { CourseCardPresaleTag } from './CourseCardPresaleTag';
import { CourseCardPromoBar } from './CourseCardPromoBar';
import { CourseCardSelectionControl } from './CourseCardSelectionControl';
import { CourseCardUnavailableBadge } from './CourseCardUnavailableBadge';
import type { CourseCardCartState, CourseCardItem } from '../types/course-card';
import {
  formatCourseCardCount,
  formatCourseCardCurrency,
  formatCourseCardModality,
  formatCourseCardPrimaryCategory,
  formatCourseCardRating,
  formatCourseCardStartDate,
  formatCourseCardTeachersSummary,
  getCourseCardPriceParts,
} from '../lib/course-card-formatters';
import { hasHtmlMarkup, sanitizeCatalogHtml } from '../lib/course-card-html';
import { resolveCourseCardHref } from '../lib/course-card-links';
import { useIsDesktopHover } from '@/shared/hooks/useIsDesktopHover';
import { useToday } from '@/shared/hooks/useToday';

interface CourseCardRowProps {
  course: CourseCardItem;
  cartState: CourseCardCartState;
  cyberActive?: boolean;
  isFavorite: boolean;
  isLoggedIn?: boolean;
  showFavoriteButton?: boolean;
  isSelectable?: boolean;
  isSelected?: boolean;
  selectionName?: string;
  selectionValue?: string;
  hideActions?: boolean;
  hidePrice?: boolean;
  prioritizeImage?: boolean;
  actionLabel?: string;
  checkoutLabel?: string;
  onCartAction: () => void;
  onCartFlashAnimationComplete?: () => void;
  onFavoriteToggle: (event: MouseEvent<HTMLButtonElement>) => void;
  onNavigate: (event: MouseEvent<HTMLAnchorElement>) => void;
  onSelectionChange?: (event: ChangeEvent<HTMLInputElement>) => void;
}

export function CourseCardRow({
  course,
  cartState,
  cyberActive,
  isFavorite,
  isLoggedIn = false,
  showFavoriteButton = true,
  isSelectable = false,
  isSelected = false,
  selectionName = 'course[]',
  selectionValue,
  hideActions = false,
  hidePrice = false,
  prioritizeImage = false,
  actionLabel = 'Inscribirme',
  checkoutLabel = 'Ir a pagar',
  onCartAction,
  onCartFlashAnimationComplete,
  onFavoriteToggle,
  onNavigate,
  onSelectionChange,
}: CourseCardRowProps) {
  const isDesktopHover = useIsDesktopHover();
  const href = resolveCourseCardHref(course);
  const modality = formatCourseCardModality(course.modality);
  const today = useToday();
  const startLabel = formatCourseCardStartDate(
    course.startDate,
    course.modality,
    course.currency,
    today,
  );
  const primaryCategoryLabel = formatCourseCardPrimaryCategory(course.primaryCategory);
  const teachersSummary = formatCourseCardTeachersSummary(course);
  const promoLabel = course.promoLabel?.trim();
  const hasRating = course.rating > 0;
  const isDisabled = course.canPurchase === false;
  const hasPromo = Boolean(promoLabel) && !isDisabled;
  const hasDiscount = Boolean(course.originalPrice && course.originalPrice > course.price);
  const priceParts = getCourseCardPriceParts(course.price, course.currency);
  const description = course.description?.trim();
  const [imageError, setImageError] = useState(false);
  const descriptionHtml =
    description && hasHtmlMarkup(description) ? sanitizeCatalogHtml(description) : null;

  const handleRowCartAction = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onCartAction();
  };

  const row = (
    <article
      className={cn(
        'adipa-course-row-layout min-h-51 border-(--color-border-subtle) group grid overflow-hidden rounded-xl border bg-white shadow-none',
        'hover:border-(--border-accent-soft) transition-[border-color,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:shadow-[0_6px_18px_rgba(19,1,63,0.08)]',
        'focus-within:border-(--border-accent-soft) focus-within:shadow-[0_6px_18px_rgba(19,1,63,0.08)] motion-reduce:transition-none',
        'lg:grid-cols-[27%_minmax(0,42%)_31%]',
      )}
    >
      {hasPromo ? (
        <CourseCardPromoBar
          className="col-span-full"
          deadline={course.promoDeadline}
          label={promoLabel as string}
        />
      ) : null}
      <div
        className={cn(
          'bg-(--color-muted) relative hidden overflow-hidden lg:block',
          isDisabled && 'grayscale',
        )}
      >
        <Link
          aria-label={`Ver detalle de ${course.title}`}
          className="absolute inset-0 cursor-pointer"
          href={href}
          onClick={onNavigate}
        >
          {course.imageUrl && !imageError ? (
            <>
              <Image
                fill
                unoptimized
                alt={course.title}
                fetchPriority={prioritizeImage ? 'high' : undefined}
                loading={prioritizeImage ? 'eager' : undefined}
                className="object-cover transition-transform duration-300 ease-out group-hover:scale-[1.035] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                sizes="(min-width: 1280px) 320px, (min-width: 1024px) 28vw, 100vw"
                src={course.imageUrl}
                onError={() => setImageError(true)}
              />
              <div className="bg-linear-to-b absolute inset-0 from-black/10 via-transparent to-black/5" />
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-adipa-surface-lavender">
              <BookOpen aria-hidden="true" className="text-(--color-primary-500) h-10 w-10" />
            </div>
          )}
        </Link>
        {/* REGLA: checkbox XOR favorito. Ambos viven en la imagen, arriba-derecha. */}
        {isSelectable ? (
          <CourseCardSelectionControl
            checked={isSelected}
            className="absolute right-3 top-3 z-10"
            label={`Seleccionar ${course.title}`}
            name={selectionName}
            value={selectionValue ?? course.id}
            onChange={(event) => onSelectionChange?.(event)}
          />
        ) : showFavoriteButton && isLoggedIn ? (
          <CourseCardFavoriteButton
            className="absolute right-3 top-3 z-10"
            isFavorite={isFavorite}
            onToggle={onFavoriteToggle}
          />
        ) : null}
      </div>

      <div className="min-w-0 px-4 pb-4 pt-3 sm:px-5 lg:pb-5 lg:pt-[13px]">
        <div className="flex min-w-0 flex-col gap-2">
          {isDisabled ? (
            <CourseCardUnavailableBadge />
          ) : course.presale || primaryCategoryLabel || course.allianceLabel ? (
            <div className="flex flex-wrap items-center gap-2">
              {course.presale ? <CourseCardPresaleTag /> : null}
              {primaryCategoryLabel ? (
                <span className="text-(--color-primary-700) border-(--color-border-subtle) inline-flex h-6 w-fit items-center rounded-lg border bg-white/95 px-3 text-[0.64rem] font-bold leading-tight backdrop-blur-sm">
                  {primaryCategoryLabel}
                </span>
              ) : null}
              {course.allianceLabel ? (
                <CourseCardAllianceTag label={course.allianceLabel} />
              ) : null}
            </div>
          ) : null}


          <Link className="w-fit max-w-full cursor-pointer" href={href} onClick={onNavigate}>
            <h3
              className={cn(
                'text-navy duration-180 group-hover:text-(--color-primary-700) hover:text-(--color-primary-700) overflow-hidden text-[1rem] font-bold leading-snug transition-colors',
                '[-webkit-box-orient:vertical] [-webkit-line-clamp:2] [display:-webkit-box]',
              )}
            >
              {course.title}
            </h3>
          </Link>

          {teachersSummary ? (
            <p className="text-(--text-secondary) text-[0.78rem]">{teachersSummary}</p>
          ) : null}

          <div className="text-(--text-secondary) space-y-1 text-[0.75rem] font-medium">
            <div className="flex min-h-5 flex-wrap items-center gap-x-2 gap-y-1">
              {hasRating ? (
                <>
                  <span className="inline-flex items-center gap-1">
                    <Star
                      aria-hidden="true"
                      className="fill-(--course-rating-star) text-(--course-rating-star) h-3 w-3"
                    />
                    <span className="text-navy tabular-nums">
                      {formatCourseCardRating(course.rating)}
                    </span>
                    <span>({formatCourseCardCount(course.studentsCount)})</span>
                  </span>
                  <span aria-hidden="true" className="text-(--color-border-strong)">
                    |
                  </span>
                </>
              ) : null}
              <span className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className={cn(
                    'relative h-1.5 w-1.5 rounded-full',
                    course.modality === 'en-vivo'
                      ? 'bg-(--course-live)'
                      : 'bg-(--color-primary-500)',
                  )}
                >
                  {course.modality === 'en-vivo' ? (
                    <span className="bg-(--course-live-soft) absolute inset-[-0.18rem] animate-ping rounded-full motion-reduce:animate-none" />
                  ) : null}
                </span>
                {modality}
              </span>
            </div>
            {startLabel && !isDisabled ? (
              <p className="flex min-h-5 items-center gap-1">
                <Calendar aria-hidden="true" className="text-muted-text h-3 w-3" />
                {startLabel}
              </p>
            ) : null}
            {course.allianceLabel ? (
              <CourseCardAllianceNote label={course.allianceLabel} />
            ) : null}
          </div>

          {descriptionHtml ? (
            <div
              className="text-(--text-secondary) line-clamp-2 text-[0.8rem] leading-relaxed"
              dangerouslySetInnerHTML={{ __html: descriptionHtml }}
            />
          ) : null}
          {!descriptionHtml && description ? (
            <p className="text-(--text-secondary) line-clamp-2 text-[0.8rem] leading-relaxed">
              {description}
            </p>
          ) : null}
        </div>
      </div>

      <div className="border-(--color-border-subtle) flex flex-col justify-center gap-4 border-t px-4 py-4 sm:px-5 lg:border-l lg:border-t-0 lg:py-5">
        <div className="flex flex-col items-stretch gap-4">
          {!isDisabled && !hidePrice ? (
            <div className="text-center">
              <div className="text-navy inline-flex max-w-full items-baseline justify-center gap-2">
                <CourseCardDealIcon
                  cyberActive={cyberActive}
                  flashDeal={course.flashDeal}
                  hasDiscount={hasDiscount}
                />
                <span className="text-[0.9rem] font-medium leading-none tracking-tight">
                  {priceParts.currencyLabel}
                </span>
                <span className="min-w-0 text-[1.72rem] font-bold leading-none tracking-tight">
                  {priceParts.amountLabel}
                </span>
              </div>

              {hasDiscount || course.discount ? (
                <div className="mt-2 flex min-h-6 items-center justify-center gap-3">
                  {hasDiscount ? (
                    <span className="text-muted-text text-[0.9rem] font-medium line-through">
                      {formatCourseCardCurrency(
                        course.originalPrice ?? course.price,
                        course.currency,
                      )}
                    </span>
                  ) : null}
                  {course.discount ? (
                    <CourseCardDiscountBadge discount={course.discount} />
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          {!hideActions && !isDisabled ? (
            <div className="bg-(--color-border-subtle) h-px" />
          ) : null}
        </div>

        {!hideActions ? (
          <div className="flex flex-wrap gap-2">
            <Link
              className="border-(--color-primary-500) text-(--color-primary-600) hover:bg-(--color-primary-500) hover:border-(--color-primary-500) hover:text-adipa-primary-foreground focus-visible:ring-(--color-primary-500) inline-flex h-11 flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border bg-white px-2.5 text-[0.82rem] font-semibold transition-[background-color,border-color,color,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:shadow-[0_10px_22px_rgba(112,78,253,0.18)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 motion-reduce:transition-none"
              href={href}
              onClick={onNavigate}
            >
              <ExternalLink aria-hidden="true" className="h-3.5 w-3.5 shrink-0" strokeWidth={2.1} />
              Ver detalle
            </Link>
            {course.canPurchase !== false ? (
              <CourseCardCartIconButton
                aria-label={
                  cartState === 'pay' ? checkoutLabel : `${actionLabel} en ${course.title}`
                }
                cartState={cartState}
                onAddingAnimationComplete={onCartFlashAnimationComplete}
                onClick={handleRowCartAction}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </article>
  );

  return (
    <PreviewCard.Root>
      <PreviewCard.Trigger closeDelay={120} delay={430} render={<div className="h-full" />}>
        {row}
      </PreviewCard.Trigger>

      {isDesktopHover ? (
        <PreviewCard.Portal>
          <PreviewCard.Positioner
            sticky
            align="start"
            className="z-[70] max-w-[calc(100vw-32px)]"
            collisionAvoidance={{ side: 'flip', align: 'shift', fallbackAxisSide: 'none' }}
            collisionPadding={{ top: 152, right: 16, bottom: 16, left: 16 }}
            positionMethod="fixed"
            side="right"
            sideOffset={14}
          >
            <PreviewCard.Popup className="origin-(--transform-origin) data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0 transition-[transform,scale,opacity] duration-150">
              <CourseCardPopover
                cartState={cartState}
                course={course}
                onCartAction={onCartAction}
                onCartFlashAnimationComplete={onCartFlashAnimationComplete}
                onNavigate={onNavigate}
              />
            </PreviewCard.Popup>
          </PreviewCard.Positioner>
        </PreviewCard.Portal>
      ) : null}
    </PreviewCard.Root>
  );
}
